import type { ComponentPropsWithRef } from 'react';
import { useCallback, useMemo } from 'react';
import { Heading, useTheme } from '@simplywallst/ui-core';
import loadable from '@loadable/component';
import ReportSection from '@components/ReportSection';
import ReportSubSection from '@components/ReportSubSection';
import ReportSectionHeader from '@components/ReportSectionHeader';
import DeferRenderUntilIntersecting from '@components/DeferRenderUntilIntersecting';
import PageAnchor from '@components/PageAnchor';
import type { Entity as InsiderTradingEntity } from '../InsiderTrading';
import type {
  OwnershipEntity,
  OwnershipDataFormatted,
} from '../OwnershipChart';
import { LegacyAnchor } from '@/constants/anchors';
import {
  COMPANY_INSIDER_TRADING,
  COMPANY_OWNERSHIP_BREAKDOWN,
} from '@/constants/feedbackSections';
import { ChartType } from '@/constants/downloadSections';
import { getCompanyInfographicDownloadUrl } from '@utilities/urlGenerator';
import Statement from '@/components/Statement';
import Description from '@/components/Statement/components/Description';
import type { InsiderTradingTransactionsProps } from '@/pages/CompanyReport/components/InsiderTradingTransactions';
import type {
  Shareholder,
  OwnerType,
} from '@/pages/CompanyReport/redux/interface/interface';
import ListTruncator from '@/components/ListTruncator';
import type { ViewMode } from '@/pages/CompanyReport/enum';
import { SubHeadingWrapper, TotalOwnership } from './styled';
import { BrazeContentCardWrapper } from '@/pages/CompanyReport/components/BrazeContentCard';
import ChartWrapper from '@/pages/CompanyReport/components/Overview/components/ChartWrapper';
import useWindowSize from '@/hooks/useWindowSize';
import CompanySectionTitle from '@/pages/CompanyReport/components/CompanySectionTitle';
import { useVisibleSectionsIsPristine } from '@/pages/CompanyReport/components/VisibleSectionsContextProvider';
import {
  useOwnershipTranslations,
  OwnershipTranslation,
} from '../../hooks/useOwnershipTranslation';
import { useCompanyCanonicalUrl } from '@/hooks/useCompany';
import useCompanyStatementsAPIFormatted from '@/hooks/useCompanyStatementsAPIFormatted';
import { getTranslationKey } from '../../utils';
import type YAxis from '../InsiderTrading/components/YAxis';
import { Postfix, Label } from '../InsiderTrading/components/YAxis/styled';
import { useLocale } from '@/hooks/useLocale';
const BrazeContentCard = loadable(() => import('@components/BrazeContentCard'));

const TopShareholders = loadable(
  () => import('@/pages/CompanyReport/components/Ownership'),
  { resolveComponent: (component) => component.TopShareholders }
);
const OwnershipChart = loadable(() => import('../OwnershipChart'));
const OwnershipTable = loadable(() => import('../OwnershipTable'));

const InsiderTradingTransactions = loadable(
  () => import('@/pages/CompanyReport/components/InsiderTradingTransactions'),
  {
    resolveComponent: (component) => component.InsiderTradingTransactions,
  }
);
const ChartActionsContainer = loadable(
  () => import('@components/ChartActions'),
  {
    ssr: false,
    resolveComponent: (component) => component.ChartActionsContainer,
  }
);

const InsiderTrading = loadable(() => import('../InsiderTrading'), {
  ssr: false,
});

export interface ShareholderWithTitle extends Shareholder {
  holdingValue: string;
  sharesHeldFormat: string;
  owner: {
    name: string;
    type: OwnerType;
    companyType: number;
    objectId: number;
    teamTitle: string;
    boardTitle: string;
  };
}
export interface OwnershipProps {
  sectionNumber?: number;
  companyId: string;
  uniqueSymbol: string;
  skipDefer: boolean;
  insiderTradingTransactions: InsiderTradingTransactionsProps[];
  topShareholders: ShareholderWithTitle[];
  totalOwnershipPercent: string;
  viewMode: ViewMode;
  /**
   * Ownership Chart
   */
  ownership: Partial<OwnershipEntity>[];
  totalOwnedShares: number;
  ownershipDataFormatted: OwnershipDataFormatted;
  /**
   * Insider Trading
   */
  insiderTrading: InsiderTradingEntity[];
}

const Ownership = ({
  sectionNumber,
  companyId,
  uniqueSymbol,
  skipDefer,
  insiderTradingTransactions,
  topShareholders,
  totalOwnershipPercent,
  viewMode,
  /**
   * Ownership Chart
   */
  ownership,
  ownershipDataFormatted,
  totalOwnedShares,
  /**
   * Insider Trading
   */
  insiderTrading,
}: OwnershipProps) => {
  const isPristine = useVisibleSectionsIsPristine();
  const subSectionTitleElement = isPristine ? 'h2' : 'h3';
  const { insiderTradingDownloadUrl, ownershipSummaryDownloadUrl } =
    useMemo(() => {
      return {
        insiderTradingDownloadUrl: getCompanyInfographicDownloadUrl(
          companyId,
          ChartType.InsiderTradingVolume
        ),
        ownershipSummaryDownloadUrl: getCompanyInfographicDownloadUrl(
          companyId,
          ChartType.OwnershipBreakdown
        ),
      };
    }, [companyId]);
  const theme = useTheme();
  const { width: screenWidth } = useWindowSize();
  const headingHasOpacity = screenWidth && screenWidth <= theme.lgMinRaw;
  const t = useOwnershipTranslations();
  const locale = useLocale();

  const canonicalUrl = useCompanyCanonicalUrl();
  const { data: statements } = useCompanyStatementsAPIFormatted({
    canonicalURL: canonicalUrl,
  });
  const { HasNetInsiderBuyingOver3Months, IsNotDilutedOverPastYear } =
    statements;

  const insiderTradingYLabelRender = useCallback<
    NonNullable<ComponentPropsWithRef<typeof YAxis>['labelRender']>
  >((series, label1Y, label2Y) => {
    return (
      <OwnershipTranslation
        i18nKey="insiderTradingVolume.chart.yAxisLabel"
        values={{
          series,
        }}
      >
        <Label y={label1Y} dominantBaseline="hanging" textAnchor="start">
          {series}
        </Label>
        <Postfix y={label2Y} dominantBaseline="hanging" textAnchor="start">
          months
        </Postfix>
      </OwnershipTranslation>
    );
  }, []);
  return (
    <ReportSection sectionNumber={sectionNumber} data-cy-id="ownership-section">
      <ReportSectionHeader
        title={<CompanySectionTitle title={t('heading')} />}
        sectionDescription={t('description')}
      />
      <BrazeContentCardWrapper>
        <BrazeContentCard
          feedType="company-overview.ownership"
          anchorTarget="_blank"
        />
      </BrazeContentCardWrapper>
      <PageAnchor
        type="sub-section-title"
        id={LegacyAnchor.OWNERSHIP_INSIDER_TRADING}
      />
      <ReportSubSection
        titleElement={subSectionTitleElement}
        title={t('subHeading1')}
        body={
          <>
            {insiderTradingTransactions.length > 0 && (
              <InsiderTradingTransactions
                data={insiderTradingTransactions}
                uniqueSymbol={uniqueSymbol}
                viewMode={viewMode}
              />
            )}
            <SubHeadingWrapper>
              {headingHasOpacity ? (
                <Heading
                  as="h4"
                  typography="baseStrong"
                  color="whiteRGBA"
                  opacity={0.5}
                >
                  {t('insiderTradingVolume.heading')}
                </Heading>
              ) : (
                <Heading as="h4" typography="baseStrong" color="white">
                  {t('insiderTradingVolume.heading')}
                </Heading>
              )}
            </SubHeadingWrapper>
            <DeferRenderUntilIntersecting
              preRenderHeight="466"
              preRenderHeightMdDown="562"
              skipDefer={skipDefer}
            >
              {() => (
                <>
                  <InsiderTrading
                    data={insiderTrading}
                    labels={{
                      individualSharesBought: t('insiderTradingVolume.label1'),
                      companySharesBought: t('insiderTradingVolume.label2'),
                      individualSharesSold: t('insiderTradingVolume.label3'),
                      companySharesSold: t('insiderTradingVolume.label4'),
                      xAxisLabel1: t('insiderTradingVolume.chart.xAxisLabel1'),
                      xAxisLabel2: t('insiderTradingVolume.chart.xAxisLabel2'),
                      yAxisLabelRender: insiderTradingYLabelRender,
                      chartLabel1: (shares) =>
                        t('insiderTradingVolume.chart.label1', {
                          shares: shares.toLocaleString(locale),
                        }),
                      chartLabel2: (value) =>
                        t('insiderTradingVolume.chart.label2', { value }),
                    }}
                  />
                  <ChartActionsContainer
                    companyName={uniqueSymbol}
                    feedbackLabel={COMPANY_INSIDER_TRADING}
                    exportDarkUrl={insiderTradingDownloadUrl}
                  />
                </>
              )}
            </DeferRenderUntilIntersecting>
          </>
        }
        statements={
          <Statement
            title={HasNetInsiderBuyingOver3Months?.title}
            description={
              <Description statement={HasNetInsiderBuyingOver3Months} />
            }
            value={HasNetInsiderBuyingOver3Months?.value}
            statementId="hasNetInsiderBuyingOver3Months"
          />
        }
      />
      <hr />
      {ownership.length > 0 && (
        <>
          <PageAnchor type="sub-section-title" id={LegacyAnchor.OWNERSHIP} />
          <ReportSubSection
            titleElement={subSectionTitleElement}
            title={t('subHeading2')}
            body={
              <ChartWrapper
                id="ownership-breakdown-chart"
                chart={
                  <OwnershipChart
                    data={ownership}
                    dataFormatted={ownershipDataFormatted}
                    totalOwnedShares={totalOwnedShares}
                    titleLabel={(key) => t(getTranslationKey(key))}
                    sharesLabel={(shares) =>
                      t('sharesLabel', {
                        shares,
                      })
                    }
                  />
                }
                table={
                  <OwnershipTable
                    data={ownership}
                    dataFormatted={ownershipDataFormatted}
                  />
                }
                preRenderHeight={336}
                actionsMarginTop={16}
                exportDarkUrl={ownershipSummaryDownloadUrl}
                feedbackLabel={COMPANY_OWNERSHIP_BREAKDOWN}
              />
            }
            statements={
              <Statement
                title={IsNotDilutedOverPastYear?.title}
                description={
                  <Description statement={IsNotDilutedOverPastYear} />
                }
                value={IsNotDilutedOverPastYear?.value}
                statementId="isNotDilutedOverPastYear"
              />
            }
          />
        </>
      )}
      <PageAnchor type="sub-section-title" id={LegacyAnchor.OWNERSHIP} />
      {topShareholders.length > 0 && (
        <>
          <hr />
          <ReportSubSection
            titleElement={subSectionTitleElement}
            title={t('subHeading3')}
            body={
              <>
                <TotalOwnership>
                  {t('topShareholder.caption', {
                    count: topShareholders.length,
                    percentage: totalOwnershipPercent,
                  })}
                </TotalOwnership>
                {topShareholders.length > 10 ? (
                  <ListTruncator viewMode={viewMode} minHeight={360}>
                    <TopShareholders
                      topShareholders={topShareholders}
                      viewMode={viewMode}
                    />
                  </ListTruncator>
                ) : (
                  <TopShareholders
                    topShareholders={topShareholders}
                    viewMode={viewMode}
                  />
                )}
              </>
            }
          />
        </>
      )}
    </ReportSection>
  );
};

export default Ownership;
